// client/src/coms/components/mobile/MobileNavMenu.js
// Phones without a bar (Dropdown and Gestures layouts): one menu with your recent conversations, your
// destinations in your order and everything else. It drops down from the header or rises from the bottom.
// MobileMenuLauncher is the slim bottom control that opens it upward (and the gesture handle, the tap alternative to
// swiping up).
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { menuDestinations } from '../../personalisation/mobileLayout';
import { NavGlyph, currentDestination, usePublishedHeight } from './MobileNavBar';
import NavIcon from './NavIcon';
import './mobile.css';
import './mobileMenu.css';

function initials(s) {
  const parts = String(s || '?').trim().split(/\s+/).filter(Boolean);
  return (parts.length > 1 ? parts[0][0] + parts[1][0] : String(s || '?').slice(0, 2)).toUpperCase();
}

function ConvChip({ item, onOpen }) {
  const [broken, setBroken] = useState(false);
  const src = item.kind === 'dm' ? item.avatarUrl : item.serverLogo;
  return (
    <button type="button" className={`vx-mmenu-conv${item.unread || item.mentions ? ' is-unread' : ''}`} onClick={() => onOpen(item)} data-testid={`mobile-menu-conv-${item.id}`}>
      <span className={`vx-mmenu-conv-pic${item.kind === 'dm' ? ' is-round' : ''}`} aria-hidden="true">
        <span>{initials(item.kind === 'dm' ? item.title : item.serverName)}</span>
        {src && !broken ? <img src={src} alt="" onError={() => setBroken(true)} /> : null}
        {item.mentions > 0 || item.unread ? <span className="vx-mmenu-conv-dot" /> : null}
      </span>
      <span className="vx-mmenu-conv-name">
        {item.kind === 'channel' ? '#' : ''}
        {item.title}
      </span>
    </button>
  );
}

export default function MobileNavMenu({ nav, direction = 'down', style = 'icons-labels', activeTab, conversations = [], onOpenConversation, onNavigate, onClose, onCustomise, badges = {} }) {
  const ref = useRef(null);
  const [top, setTop] = useState(0);
  const { mine, rest } = menuDestinations(nav);
  // A channel belongs to Servers here unless "Last channel" is one of your destinations.
  const mineIds = mine.map((o) => o.id);
  const all = [...mineIds, ...rest.map((o) => o.id)].filter((id) => id !== 'chat' || mineIds.includes('chat'));
  const current = currentDestination({ activeTab, items: all });
  // Words always accompany the pictures in a menu; the chosen style decides icon or emoji.
  const glyphStyle = style.startsWith('emoji') ? 'emoji-labels' : style === 'labels' ? 'labels' : 'icons-labels';

  // Dropping down: start right under the header.
  useLayoutEffect(() => {
    if (direction !== 'down') return;
    const h = document.querySelector('[data-testid="mobile-header"]');
    setTop(h ? Math.round(h.getBoundingClientRect().bottom) : 0);
  }, [direction]);

  useEffect(() => {
    const first = ref.current && ref.current.querySelector('button');
    if (first) first.focus({ preventScroll: true });
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const item = (o) => (
    <button
      key={o.id}
      type="button"
      className={`vx-mmenu-item${current === o.id ? ' is-on' : ''}`}
      aria-current={current === o.id ? 'page' : undefined}
      onClick={() => onNavigate(o.id)}
      data-testid={`mobile-menu-${o.id}`}
    >
      <NavGlyph id={o.id} variant={glyphStyle} size={22} badge={badges[o.id]} />
    </button>
  );

  return (
    <div
      className={`vx-mmenu-backdrop is-${direction}`}
      style={direction === 'down' ? { top } : undefined}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="mobile-menu"
    >
      <div className={`vx-mmenu is-${direction}`} role="dialog" aria-modal="true" aria-label="Navigation" ref={ref}>
        {direction === 'up' ? <div className="vx-msheet-grip" aria-hidden="true" /> : null}
        {conversations.length ? (
          <section className="vx-mmenu-section">
            <h3 className="vx-mmenu-heading">Conversations</h3>
            <div className="vx-mmenu-convs" data-coms-no-swipe="true">
              {conversations.slice(0, 8).map((c) => (
                <ConvChip key={c.id} item={c} onOpen={onOpenConversation} />
              ))}
            </div>
          </section>
        ) : null}
        <section className="vx-mmenu-section">
          <h3 className="vx-mmenu-heading">Go to</h3>
          <div className="vx-mmenu-grid">{mine.map(item)}</div>
        </section>
        {rest.length ? (
          <section className="vx-mmenu-section">
            <h3 className="vx-mmenu-heading">More</h3>
            <div className="vx-mmenu-grid is-small">{rest.map(item)}</div>
          </section>
        ) : null}
        <button type="button" className="vx-btn small vx-msheet-customise" onClick={onCustomise} data-testid="mobile-menu-customise">
          Customise navigation…
        </button>
      </div>
    </div>
  );
}

// The bottom control for Dropdown-from-the-bottom (a labelled launcher) and Gestures (a small handle). Both open the
// menu upward on a tap, so nothing depends on a swipe.
export function MobileMenuLauncher({ kind = 'launcher', open, label, onToggle, publish = true }) {
  const ref = useRef(null);
  usePublishedHeight(ref, publish);
  if (kind === 'handle') {
    return (
      <div ref={ref} className="vx-mhandle-wrap">
        <button type="button" className={`vx-mhandle${open ? ' is-open' : ''}`} onClick={onToggle} aria-expanded={!!open} aria-label="Open navigation" data-testid="mobile-menu-toggle">
          <span className="vx-mhandle-bar" aria-hidden="true" />
        </button>
      </div>
    );
  }
  return (
    <div ref={ref} className="vx-mlaunch-wrap">
      <button type="button" className={`vx-mlaunch${open ? ' is-open' : ''}`} onClick={onToggle} aria-expanded={!!open} aria-haspopup="menu" data-testid="mobile-menu-toggle">
        <NavIcon id="more" size={18} />
        <span className="vx-mlaunch-label">{label}</span>
        <span className="vx-mlaunch-caret" aria-hidden="true">
          <NavIcon id="up" size={16} />
        </span>
      </button>
    </div>
  );
}
